import { FC, useEffect, useMemo, useState } from 'react';
import { SubmitButton } from '@/components/form/submit-button';
import { Button } from '@/components/ui/button';
import { strings } from '@/constants/strings';
import { useForm } from '@/forms/hooks/use-form';
import { UserFieldValues } from '@/forms/schemas/user/field-values';
import { buildUserFormSchema } from '@/forms/schemas/user/schema';
import { useGroupSelectOptions } from '@/hooks/use-group-select-options';
import { useOrganisationSelectOptions } from '@/hooks/use-organisation-select-options';
import { useRoleSelectOptions } from '@/hooks/use-role-select-options';
import { DrawerContentFormLayout } from '@/layouts/drawer-content-form-layout';
import { ApplicationRole } from '@/types/application-role';
import { ApplicationUser } from '@/types/application-user';
import { Group } from '@/types/group';
import { Organisation, OrganisationIdAndNameOnly } from '@/types/organisation';
import { getPlatformAdminRoleId } from '@/utils/get-platform-admin-role-id';
import { isUserPlatformAdmin } from '@/utils/users/is-user-platform-admin';
import { canActiveUserDeleteUser } from '@/utils/users/permissions/can-active-user-delete-user';

export type EditUserFormProps = {
  activeUser: ApplicationUser;
  canAssignAnyOrganisation: boolean;
  currentOrganisation: OrganisationIdAndNameOnly | undefined;
  permittedGroupValues: Group[];
  organisations: Organisation[];
  groups: Group[];
  roles: ApplicationRole[];
  user: ApplicationUser;
  onCancel: () => void;
  onDelete: () => void;
  onSubmit: (values: UserFieldValues) => void;
};

export const EditUserForm: FC<EditUserFormProps> = ({
  activeUser,
  canAssignAnyOrganisation,
  currentOrganisation,
  permittedGroupValues,
  organisations,
  groups,
  roles,
  user,
  onCancel,
  onDelete,
  onSubmit,
}) => {
  const isActiveUserPlatformAdmin = isUserPlatformAdmin(activeUser);
  const platformAdminRoleId = getPlatformAdminRoleId(roles);
  const [isPlatformAdminRoleSelected, setIsPlatformAdminRoleSelected] =
    useState(isUserPlatformAdmin(user));
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<
    number | undefined
  >(user.organisation?.id ?? currentOrganisation?.id);
  const roleOptions = useRoleSelectOptions(roles);
  const organisationOptions = useOrganisationSelectOptions(organisations);
  const filteredGroups = useMemo(
    () =>
      selectedOrganisationId
        ? groups.filter(
            (group) => group.organisation.id === selectedOrganisationId,
          )
        : groups,
    [groups, selectedOrganisationId],
  );
  const groupOptions = useGroupSelectOptions(filteredGroups);
  const permittedGroupValueOptions =
    useGroupSelectOptions(permittedGroupValues);
  const formSchema = useMemo(
    () =>
      buildUserFormSchema(
        {
          type: 'edit',
          activeUser,
          activeUserIsPlatformAdmin: isActiveUserPlatformAdmin,
          canAssignAnyOrganisation,
          isPlatformAdminRoleSelected,
          organisationName: currentOrganisation?.name ?? '',
          organisationOptions,
          groupOptions,
          roleOptions,
          permittedGroupValueOptions,
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          roleId: user.role.id,
          roleName: user.role.roleName,
          organisationId: user.organisation?.id,
          permittedGroups: (user.permittedGroups ?? []).map(
            (group) => group.id,
          ),
        },
      ),
    [
      activeUser,
      canAssignAnyOrganisation,
      currentOrganisation?.name,
      isActiveUserPlatformAdmin,
      isPlatformAdminRoleSelected,
      organisationOptions,
      roleOptions,
      groupOptions,
      user.firstName,
      user.lastName,
      user.email,
      user.role.id,
      user.role.roleName,
      user.organisation?.id,
      user.permittedGroups,
      permittedGroupValueOptions,
    ],
  );

  const { fieldItems, formState, handleSubmit, trigger, watch } = useForm({
    schema: formSchema,
    onSubmit,
  });

  if (formState.submitCount > 0) {
    trigger();
  }

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'roleId') {
        setIsPlatformAdminRoleSelected(value.roleId === platformAdminRoleId);
      }
      if (name === 'organisationId') {
        setSelectedOrganisationId(
          typeof value.organisationId === 'number'
            ? value.organisationId
            : undefined,
        );
      }
    });
    return () => subscription.unsubscribe();
  }, [platformAdminRoleId, setIsPlatformAdminRoleSelected, watch]);

  const canDeleteUser = canActiveUserDeleteUser(activeUser, user);
  return (
    <DrawerContentFormLayout
      buttonItems={[
        canDeleteUser ? (
          <Button key="delete" onClick={onDelete} variant="primary-warning">
            {strings.users.drawer_edit_form.delete}
          </Button>
        ) : null,
        <div className="flex-grow" key="flex"></div>,
        <Button key="cancel" onClick={onCancel} variant="secondary">
          {strings.users.drawer_edit_form.cancel}
        </Button>,
        <SubmitButton
          key="submit"
          text={strings.users.drawer_edit_form.submit}
        />,
      ]}
      fieldItems={fieldItems}
      onSubmit={handleSubmit}
    />
  );
};
